import React, { useState } from "react";

function Changelog() {
  const [mode, setMode] = useState("release"); // "release" | "change"
  const [selectedVersion, setSelectedVersion] = useState("all");

  const versions = [
    "all",
    "v6.4",
    "v6.3",
    "v6.2",
    "v6.1",
    "v6.0",
    "v5.6",
    "v5.5",
    "v5.4",
    "v5.3",
    "v5.2",
    "v5.1",
    "v5.0",
    "v4.1",
    "v4.0",
    "v3.2",
    "v3.1",
    "v3.0",
    "v2.0",
    "v1.0",
  ];

  // --- Release notes data (user perspective) ---
  const releaseEntries = [
    {
      version: "v6.4",
      date: "September 16, 2026",
      type: "minor",
      goal: "Make it easy to export and archive everything about a report card, semester review or apprentice at once, give trainers a clearer way to reuse previous comments, and tighten Quick Notes to the people who should actually see them.",
      highlights: [
        "New “Export all reports” button next to a learner's report cards: downloads every active report as a single ZIP file with one click, or lets you pick exactly which ones to include first.",
        "The apprentice's semester review list gets the same one-click export: all Bildungsberichte and Semestergespräche for that apprentice as one ZIP, or a custom selection.",
        "New “Export Snapshot” action in the Apprentice Directory: a single PDF with an apprentice's profile, semester review history, bonus history, final grades and current-semester grades, styled like the existing semester review PDF.",
        "The “previous comments” dropdown in the semester review now clearly distinguishes whose comments you're looking at: your own previous report, or the apprentice's own comments, instead of one unlabelled list.",
        "A new optional “Prefill fields” action copies the selected previous comments into empty fields as real, editable text, only when you explicitly ask for it. It never touches a field that already has content.",
      ],
      improvements: [
        "The export selection dialogs show how many reports are selected and disable the export button when nothing is selected or nothing is available.",
        "Both export flows reuse the exact same PDF generation already used for individual downloads, so a ZIP export and a single download always look identical.",
        "The previous-comments dropdown now shows “No previous report available” instead of an empty or confusing choice when nothing exists yet.",
      ],
      fixes: [
        "Quick Notes are now only visible to Berufsbildner (and Admin); Practice Instructors could previously see them even though the feature was meant to be Berufsbildner-only.",
      ],
    },
    {
      version: "v6.3",
      date: "August 5, 2026",
      type: "minor",
      goal: "Let a person work across several professions, put the reviewer's open cases first, and make the whole application speak one language with one consistent look.",
      highlights: [
        "A user can now be responsible for more than one profession. The old placeholder “Both Professions” is gone: instead of a single fixed pairing, a practice instructor or vocational trainer can cover any combination, for example Informatik and KV.",
        "Everyone is now called an apprentice. “Lernende”, “Learner” and “Student” have been replaced everywhere in the interface, and “PRACTICE_INSTRUCTOR” now reads “Practice Instructor” instead of an all-caps role code.",
        "Notification preferences: apprentices and trainers choose per person whether they want to be notified about insufficient grades, monthly reports and semester reports.",
        "A rejected semester is now announced with a pop-up when the apprentice logs in, instead of an email that was easy to miss. The notice links straight to the affected grades.",
        "Deleting a user finally works in one step. Previously the deletion stopped halfway on users who had data, leaving the account behind. It now removes the person and everything belonging to them after a second confirmation that lists exactly what will be deleted.",
      ],
      improvements: [
        "The reviewer dashboard leads with the number of open cases, ordered by how long each one has been waiting, and flags cases waiting more than 14 days or missing a report card.",
        "Undoing a check now asks for confirmation and states the consequences, including that the record of who checked it is cleared.",
        "Bonus pages show one semester at a time as a fold-out section, opening on the current semester instead of listing every semester at once.",
        "Admins and vocational trainers can choose explicitly which education report and which semester review feed the bonus calculation, instead of only seeing the automatic pick.",
        "Definitive grades now show the module code in front of the subject name, for example “M164 - Datenbanken abfragen”.",
        "The apprentice's review widget on the dashboard only shows the current and previous semester; everything older stays available under “View all”.",
        "The edit dialog only shows the fields a role actually owns: a practice instructor gets a department, an apprentice never does, and a reviewer only their name. Names can be corrected for every role.",
        "Roles that cannot describe the same person can no longer be combined; only Admin and Berufsbildner can be held together.",
        "Subject import: a template can be downloaded, the subject code is optional, the unused URL column is ignored and every column is explained in the dialog.",
        "All dialogs across the application share one look and behave the same way: they close with Escape or a click outside, and the actions always sit in the same place.",
        "The dashboard now names the class instead of the school for the current semester, so apprentices in two classes see both.",
      ],
      fixes: [
        "A failed deletion no longer destroys data: the application now checks first and leaves the account untouched if it cannot finish.",
        "Saving a Berufsbildner no longer wipes their profession.",
        "The subject import no longer skips faulty rows silently; every row that cannot be imported is reported with its line number.",
        "Avatar initials are no longer spaced or lower case, and no longer show a single letter where two were intended.",
        "Long entries in the sidebar no longer wrap onto a second line.",
      ],
    },
    {
      version: "v6.2",
      date: "July 29, 2026",
      type: "minor",
      goal: "Make grade confirmations class-specific, simplify the reviewer workflow down to a single completion step and protect uploaded report cards from being deleted.",
      highlights: [
        "A grade confirmation now belongs to exactly one apprentice, class and semester: an apprentice attending two classes in the same semester gets two separate entries.",
        "Reviewers complete a review with “checked” only; the second step “finalized”, which was never reachable, has been removed.",
        "Apprentices archive report cards instead of deleting them. The documents stay fully available to vocational trainers and reviewers, and only admins can remove them permanently.",
        "Archived report cards can be restored by the apprentice at any time.",
      ],
      improvements: [
        "A semester can only be confirmed once a definitive grade has been saved for every subject. Missing subjects are listed by name.",
        "The confirmation card now shows the uploaded report card with filename and date, along with the reviewer's check details.",
        "Status colours in the reviewer area are consistent: checked is green, rejected is now clearly red.",
      ],
      fixes: [
        "Existing confirmations without a class reference are migrated; cases that cannot be resolved unambiguously are kept and logged for manual review.",
        "Duplicate confirmations for the same class and semester were archived and then cleaned up.",
        "Profession values in the database were aligned with the current naming, so saving profiles and classes no longer fails.",
      ],
    },
    {
      version: "v6.1",
      date: "June 15, 2026",
      type: "minor",
      goal: "Support day-to-day semester work with quick notes, simplify the assignment of apprentices to practice instructors and stabilise the application through comprehensive tests.",
      highlights: [
        "New quick notes per semester: notes can be created and edited directly in the apprentice list and in the semester review.",
        "Practice instructors can now find apprentices through a search and assign them directly, including bulk assignment.",
        "Semester reviews are shown or hidden depending on the profession.",
      ],
      improvements: [
        "Bonus detail pages use a unified semester card view and are easier to read.",
        "Profile, password and validation texts were unified and made easier to understand.",
        "Class and subject management were made more stable and faster.",
      ],
      fixes: [
        "Fixed an error in the subject type selection of the creation dialog.",
        "Manual semester overrides in the bonus are processed more reliably.",
      ],
    },
    {
      version: "v6.0",
      date: "June 1, 2026",
      type: "major",
      goal: "Deliver bonus calculation v6.0 with a new bonus architecture, clear role-based flows and transparent insight into bonus figures per semester.",
      highlights: [
        "New bonus area with a landing page, a personal bonus view as well as list and detail pages for apprentices.",
        "Bonus calculation fully reworked: semester-based calculation with stable processing of confirmed/finalized grades.",
        "Manual overrides and grouped bonus configurations were integrated so that corrections are possible in a controlled way.",
        "Explanatory videos are now available on a dedicated page linked in the footer, separated by role.",
      ],
      improvements: [
        "Status display improved: overridden states are correctly treated as completed.",
        "Bonus overviews show more detailed semester amounts and are easier to follow in daily use.",
        "Visibility of apprentices in the bonus context was refined by role and area.",
      ],
      fixes: [
        "Added null/empty checks in the calculation services to avoid runtime errors.",
        "PDF filenames in the semester review export are sanitised more robustly.",
      ],
    },
    {
      version: "v5.6",
      date: "April 23, 2026",
      type: "minor",
      goal: "Make password and onboarding processes safer, introduce the reviewer workflow and allow semester completions to be confirmed cleanly together with report cards.",
      highlights: [
        "New reviewer role with a dedicated dashboard for checking semester completions and report cards.",
        "Semesters can now be confirmed definitively, including report card upload and clear feedback on rejection.",
        "Admins can reset passwords and, if needed, force a password change on the next login.",
        "Invitations now also support reviewers and capture additional registration data more cleanly.",
      ],
      improvements: [
        "Password change, password strength and validation were improved in registration, invitation and profile.",
        "Class and school management create semesters automatically based on the training duration.",
        "Before deleting a class, the affected apprentices, assignments and data are shown up front.",
        "Subject selection, semester tabs and grade views were made more robust and clearer.",
        "Frontend terminology was unified consistently from “Student” to “Apprentice”.",
      ],
      fixes: [
        "LDAP login synchronises local password data more reliably after a successful sign-in.",
        "Lists and detail views load larger numbers of apprentices more stably.",
        "Reviewer rejections create notifications and status changes consistently.",
      ],
    },
    {
      version: "v5.5",
      date: "April 7, 2026",
      type: "minor",
      goal: "Make semester meetings and reports more reliable and user-friendly and cleanly fix the monthly grade report bug in production.",
      highlights: [
        "Investigated and fixed the production bug in the monthly grade report so that recorded grades appear correctly in the monthly email.",
        "Semester meetings now show earlier answers as placeholders, similar to the training report.",
        "Every yes/no question in the semester meeting has a new comment field for ongoing notes throughout the year.",
      ],
      improvements: [
        "Previous semester meetings can be selected correctly as a source for placeholders.",
        "Dropdowns in the semester meeting and the training report were cleaned up visually and no longer show a duplicate arrow.",
        "Continuous documentation in the semester meeting was made considerably more practical for everyday use.",
      ],
      fixes: [
        "Fixed a bug where the monthly grade report was rendered without grades in production.",
        "Fixed an error that permanently displayed “No previous meetings” in the semester meeting.",
      ],
    },
    {
      version: "v5.4",
      date: "March 23, 2026",
      type: "minor",
      goal: "Manage invitations more efficiently and use them directly within the assignment process.",
      highlights: [
        "Invitation links can now be deleted directly.",
        "New invitation links can be generated right when assigning an apprentice.",
      ],
      improvements: [
        "When creating an invitation link via “Assign apprentice”, the practice instructor is automatically linked to the corresponding apprentice.",
        "The invitation workflow was simplified and integrated better into existing processes.",
      ],
      fixes: ["Fixed an error when generating invitation links."],
    },
    {
      version: "v5.3",
      date: "March 17, 2026",
      type: "minor",
      goal: "Manage invitations more easily, present the instructor timeline more compactly and make navigation in semester reviews considerably more pleasant.",
      highlights: [
        "Added a new invitations page for creating and managing invitation links for vocational trainers and practice instructors.",
        "The instructor timeline was made more compact and open assignments are now clearly highlighted in orange.",
        "Back navigation in semester reviews now correctly returns to the previously opened apprentice instead of an empty overview.",
      ],
      improvements: [
        "Invitation links can be displayed clearly, copied and recognised as active or persistent.",
        "Timeline entries are denser, easier to read and visually better grouped.",
        "Unassigned areas are more visible and quicker to grasp.",
      ],
      fixes: [
        "Corrected the redirect behaviour in semester reviews so that the selected apprentice is preserved when navigating back.",
      ],
    },
    {
      version: "v5.2",
      date: "March 9, 2026",
      type: "minor",
      goal: "Integrate PlanPilot better into Journey, present emails more professionally and make the practice instructor timeline clearer.",
      highlights: [
        "PlanPilot is now embedded into the platform more cleanly via iframe.",
        "Emails are now sent with a correctly configured sender.",
        "Introduced a new, more professional email template for system notifications.",
        "The instructor timeline was improved visually and in content.",
      ],
      improvements: [
        "More stable iframe rendering with better behaviour for sizing, scrolling and embedding.",
        "Emails feel clearer, more modern and more on-brand.",
        "Timeline entries are easier to read and structured more cleanly over time.",
      ],
      fixes: [
        "Fixed the email sender error.",
        "Reduced rendering problems with embedded external content.",
      ],
    },
    {
      version: "v5.1",
      date: "February 16, 2026",
      type: "minor",
      goal: "Make the training report and semester meeting easier to use: less typing, correct bonus calculation and safer form filling through autosave. Also includes a number of bug fixes.",
      highlights: [
        "Remarks from the last training report are carried over as placeholders.",
        "The bonus in the training report is summed up automatically (8/5/3/0).",
        "The semester meeting is leaner: apprenticeship year, date and no signature field.",
      ],
      improvements: [
        "The overall assessment can be actively ticked off in the semester meeting.",
        "Autosave when leaving the tab and/or a warning popup for unsaved changes.",
      ],
      fixes: [],
    },
    {
      version: "v5.0",
      date: "February 11, 2026",
      type: "major",
      goal: "Provide practice instructor assignments, clean access rights and new training features (professions, report card, LDAP fix) for clear responsibilities.",
      highlights: [
        "Apprentices can be assigned to a practice instructor, including assignment history.",
        "Practice instructors only see their currently assigned apprentices (also protected on the URL level).",
        "Semester reviews: own past reviews stay readable, new reviews are only possible with an active assignment.",
        "Report card upload was added.",
      ],
      improvements: [
        "Grades of currently assigned apprentices are available read-only to practice instructors (including semester filter).",
        "Simple view vs. expert view can be toggled and is stored per user.",
        "Semester review labels were unified and made easier to understand.",
        "All professions were implemented cleanly and are taken into account throughout.",
        "LDAP login fetches the correct name directly (correct display after login).",
      ],
      fixes: [],
    },
    {
      version: "v4.1",
      date: "January 13, 2026",
      type: "minor",
      goal: "Consistent grade presentation, extended analytics and automated communication for more transparency and efficiency.",
      highlights: [
        "New analytics page with averages across all semesters per subject.",
        "The overall chart of all grades, analogous to the admin view, is now available to apprentices as well.",
        "Automatic emails for insufficient grades and a monthly grade overview.",
      ],
      improvements: [
        "Unified colour scale for grades across the entire system.",
        "Grade averages are rounded consistently to two decimal places.",
        "Simplified navigation between apprentices in the detail view.",
        "Introduced alias names for learning documentation links.",
        "Clear separation of views by role and profession.",
      ],
      fixes: [
        "Vocational trainers only see apprentices of their own profession, except for the admin role.",
      ],
    },
    {
      version: "v4.0",
      date: "December 3, 2025",
      type: "major",
      goal: "Introduced core features for learning documentation and semester meetings, including first workflows and PDF export.",
      highlights: [
        "Apprentices can now store a learning documentation link.",
        "Practice and vocational trainers can open the learning documentation directly from the profile.",
        "Semester meetings can be recorded, saved and versioned.",
        "PDF export for completed meetings was added.",
      ],
      improvements: [
        "Implemented the first form for goals, strengths, measures and free text.",
        "Meeting status clearly visible (draft, in progress, completed).",
      ],
      fixes: [],
    },
    {
      version: "v3.2",
      date: "November 20, 2025",
      type: "minor",
      goal: "Improved usability, clearer grade display and faster profiles for a smooth daily routine.",
      highlights: [
        "The overall chart now shows all grades of an apprentice in one clear graphic.",
        "The current semester is selected automatically when opening the grade view.",
        "Mobile usage was improved considerably thanks to an optimised layout and offcanvas navigation.",
      ],
      improvements: [
        "Direct search for apprentices in the overview.",
        "The icon for the lowest grade was reworked and unified.",
        "Text labels of the grade scale can be adjusted freely.",
        "Profile pages load faster through simple caching.",
      ],
      fixes: [
        "The “Change Log” section was renamed to “Release Log” and extended with version 3.2.",
      ],
    },
    {
      version: "v3.1",
      date: "November 3, 2025",
      type: "minor",
      goal: "Finishing touches for production use: company login, better analytics and optimised usability.",
      highlights: [
        "Login with a company account (LDAP / SSO) is possible.",
        "Grade history and an overall chart across all subjects are visible.",
        "The current semester is set automatically as a filter in the grade view.",
      ],
      improvements: [
        "Apprentices can be searched directly in the overview.",
        "The symbol for the lowest grade is clearly and consistently visible.",
        "Text labels of the grade scale can be adjusted (e.g. “excellent” instead of “very good”).",
        "Profile pages load faster thanks to simple caching.",
      ],
      fixes: [
        "Improved general availability and stability of the application.",
        "The “Change Log” section was renamed to “Release Log” and version 3.1 was documented.",
      ],
    },
    {
      version: "v3.0",
      date: "October 16, 2025",
      type: "major",
      goal: "Make grade management fully usable – with semesters, global subject lists and a class overview.",
      highlights: [
        "Grade entry works correctly and stays stored per semester.",
        "Apprentices see the average per subject and semester.",
        "Vocational trainers get a class overview with averages and semester comparison.",
      ],
      improvements: [
        "Global subject management for vocational trainers ensures a consistent selection.",
        "Simple charts support the visualisation of performance.",
      ],
      fixes: [
        "Admins can view the grades recorded for apprentices.",
        "Env setup and dev server were set up to simplify development.",
      ],
    },
    {
      version: "v2.0",
      date: "September 25, 2025",
      type: "major",
      goal: "Provide roles, classes and navigation so that the platform can be used school-wide.",
      highlights: [
        "Added class management and school management.",
        "Implemented role-based navigation for ADMIN, VOCATIONAL TRAINER and APPRENTICE.",
      ],
      improvements: [
        "Improved responsive design for navigation and layout.",
        "Introduced Tailwind CSS and modern UI components.",
      ],
      fixes: [],
    },
    {
      version: "v1.0",
      date: "September 10, 2025",
      type: "initial",
      goal: "Provide a minimal working system with login, registration and a database foundation.",
      highlights: [
        "Registration with master data (first name, last name, username, password, email).",
        "Login with JWT authentication.",
        "Profile page showing the master data of the signed-in user.",
      ],
      improvements: ["Built the basic role model and database structure."],
      fixes: [],
    },
  ];

  // --- Technical change log (developer perspective) ---
  const changeLogEntries = [
    {
      version: "v6.4",
      date: "September 16, 2026",
      sections: {
        Backend: [
          "Report ZIP export: ReportService.getReportsForExport(learnerId, reportIds) and buildReportsZip(reports) build a java.util.zip archive from existing ReportDocument rows (filename dedupe, no new dependency); new endpoints GET /me/reports/export-zip and GET /learners/{learnerId}/reports/export-zip in ReportController reuse checkSupervisorAccess and FeatureAccessService exactly as the single-report download does.",
          "Semester review ZIP export: new GET /api/semester-reviews/learners/{learnerId}/export-zip in SemesterReviewController reuses SemesterReviewService.getTrainerReviews for the visible-review list and SemesterReviewPdfService.generateReviewPdf per review, so no PDF generation logic was duplicated.",
          "PreviousReportDTO gained authorRole and authorName, resolved via a new shared SemesterReviewService.resolveAuthorRole(User) used by both the trainer overview mapping and the previous-report mapping, so the frontend can group comment sources by actual author role instead of assuming trainer authorship.",
          "SemesterNoteController's GET /api/semester-notes tightened from hasAnyRole('ADMIN','BERUFSBILDNER','PRAXISBILDNER','PRACTICE_INSTRUCTOR') to hasAnyRole('ADMIN','BERUFSBILDNER'), matching the write endpoints.",
          "New Apprentice Snapshot feature: ApprenticeSnapshotDTO (dto/snapshot), ApprenticeSnapshotService orchestrating BonusService.getStudentDetail, SemesterGradeConfirmationService.getStatusForLearnerAndSemester and raw Grade records for current-semester detail, ApprenticeSnapshotPdfService rendering the DTO with the same brand colors/fonts/card and footer styling as SemesterReviewPdfService, and ApprenticeSnapshotController exposing GET /api/apprentices/{learnerId}/snapshot/pdf (ADMIN or profession-scoped BERUFSBILDNER only). The export is read-only: it never triggers bonus recalculation or grade confirmation.",
        ],
        Frontend: [
          "reportService.exportReportsAsZip and semesterReviewService.exportLearnerReviewsAsZip add the blob-download + Content-Disposition-filename pattern already used elsewhere, driving new split-button + selection-modal UI in ReportsList.js and TrainerSemesterReviewsPage.js (reusing the shared Modal/ModalPrimaryButton/ModalSecondaryButton components).",
          "SemesterReviewDetailPage.js: the previous-comments dropdown now groups sources into optgroups by actual author role (\"Own comments\", \"Practice Instructor comments\", \"Learner comments\", etc.), computed by comparing each source's authorRole against the signed-in user's own role category. Added a prefillEnabled checkbox and an explicit \"Apply comments\" action that merges the same placeholders map already used for the gray hint text into only the currently empty fields - existing content and already-applied values are never overwritten, and changing the source only updates the preview until Apply is clicked again.",
          "canViewNotes in SemesterReviewDetailPage.js decoupled from the broader isTrainerView role check and now uses its own ADMIN/BERUFSBILDNER-only role list, mirroring the backend.",
          "New \"Export Snapshot\" action added to the Apprentice Directory's per-row menu (StudentList.js), between Reset Password and Archive Student, with a loading/disabled state while the export request is in flight.",
        ],
        "UI / UX": [
          "Both new ZIP export flows follow one consistent pattern across Reports and Semester Reviews: a split button (\"Export all reports\" + a small settings icon), a selection modal with Select all/Deselect all and a live selected-count, and a dynamic \"Export N reports\" primary button.",
          "The previous-comments helper text and the new prefill checkbox stay visually secondary (small, muted) so the default placeholder-only behaviour remains the obvious path.",
        ],
      },
    },
    {
      version: "v6.3",
      date: "August 5, 2026",
      sections: {
        Backend: [
          "[US77] Multi-profession support: new user_professions join table plus ProfessionScopeService (scopeOf, covers, coversLearner, selectableProfessions, defaultProfession, assignProfessions). users.profession_type stays as the primary profession; the scope is the full set a user is responsible for.",
          "[US77.1] UserController and the list/visibility queries resolve apprentices through the profession scope instead of a single profession_type equality check; UserRepository gained findByProfessionTypesAndRoleName.",
          "[US78] Notification preferences: NotificationPreference entity, repository, NotificationPreferenceService and NotificationPreferenceController (GET/PUT /api/notification-preferences/me, GET .../routing) for insufficient grades, monthly and semester reports.",
          "[US79] Rejection notices are delivered in-app: GET /api/semester-confirmations/pending-rejections returns the open rejections for the signed-in apprentice; the rejection email was removed.",
          "[US80] New UserPurgeService: a single transactional native-SQL purge across every table referencing a user, with schema-guarded statements, file cleanup for learning documentation and reassignment of NOT NULL authorship columns (subjects.created_by, classes.created_by) to the acting admin.",
          "[US80.1] DELETE /users/{id} is now non-destructive when it cannot finish: it returns requiresForce plus a per-table inventory instead of deleting rows one repository at a time. DELETE /users/{id}/force runs the purge and is open to BERUFSBILDNER for practice instructors.",
          "[US81] RevisorService.DefinitiveGradeItemDTO carries subjectCode (both the primary and the fallback resolution path).",
          "[US81.1] The revisor dashboard queue is ordered by confirmation date ascending (longest waiting first) with the limit raised to 20, so the head of the queue is the most urgent work rather than the newest arrival.",
          "[US82] BonusStudentDetailResponse and BonusLearnerDetailResponse expose currentSemesterNumber; BonusGradeItem carries subjectCode, resolved from the subject because the stored debug payload predates it.",
        ],
        Frontend: [
          "[US83] New shared dialog shell components/common/Modal.js with ModalPrimaryButton and ModalSecondaryButton: fixed header, scrolling body, fixed footer, five sizes and tones, Escape and backdrop dismissal, body scroll lock and role=\"dialog\".",
          "[US83.1] Migrated to the shell: ConfirmationModal, ArchiveUserModal, ResetLearnerPasswordModal, LeaveProtectionModal, GenerateInviteModal, EmailPreviewModal, Add/Edit/Delete grade modals, RemoveSubjectModal, UserList delete and edit dialogs, SchoolClassManagement school and class dialogs, AssignLearnerModal, PracticeInstructorSelectModal, ExpertAssignmentView and the practice instructor unassign confirmations.",
          "[US83.2] Normalised the remaining dialogs: one backdrop (bg-black/50 with blur, previously six variants) and one panel treatment (rounded-2xl, shadow-brand-xl, border-pale-dawn, previously eighteen).",
          "[US84] stringUtils.formatRoleName is now the single source of truth for role wording (ROLE_ prefix and {name}/{authority} shapes tolerated); Profile, InvitationsPage and UserList delegate to it instead of keeping three conflicting local maps.",
          "[US84.1] stringUtils.getInitials extended to first plus last name with a username fallback, always upper case and unspaced; applied to every avatar (ClassDetails, UserList, StudentDetail, PracticeInstructorAssignments, AssignLearnerModal, BonusStudentDetailPage).",
          "[US85] subjectImportService rewritten: header-aware column mapping, code optional with derivation from the title, unknown columns ignored, per-line error reporting and buildTemplateCsv() shared with the download button.",
          "[US86] Bonus semester cards became accordions driven by currentSemesterNumber with a latest-semester fallback; ReviewSelector became a visible select that also appears for a single candidate and in the missing-source state.",
          "[US86.1] bonusService.formatSubjectLabel renders \"code - name\" and is shared by MyBonusPage and BonusStudentDetailPage.",
          "[US87] LearnerSemesterReviewWidget filters to the current and previous semester on a comparable HS/FS scale with a date fallback, sorted newest first.",
          "[US88] UserEditModal fields are driven by the roles ticked in the dialog, so the form reshapes as roles change; role selection enforces that only ADMIN and BERUFSBILDNER combine.",
          "[US89] LearnerDashboard groups the current-semester card by class instead of school, so a learner in two classes gets a row for each.",
          "[US90] New tests: subjectImportService.test.js plus extended suites for UserList, RevisorReviewsPage, RevisorDashboardPage, MyBonusPage, BonusStudentDetailPage, LearnerSemesterReviewWidget, LearnerDashboard and SubjectImportModal (417 tests total).",
        ],
        "UI / UX": [
          "Renamed LERNENDE/LEARNER to Apprentice and PRACTICE_INSTRUCTOR/PRAXISBILDNER to Practice Instructor across roughly 30 display strings; German remains only where it is the domain term (Bildungsbericht, Semestergespräch, Berufsbildner, Lehrjahr).",
          "Replaced the solid red section banners with the Management Hub treatment (light bar, red icon, neutral count chip) in the apprentice directory, invitations and class details.",
          "Rebuilt the reviewer dashboard around the open queue: a leading open-case card with waiting time and overdue flags, a queue with per-entry urgency badges, and the remaining counters demoted to a background list.",
          "Removed “Delete All Subjects” from the subject import dialog, where it did not belong.",
          "Compacted the learner dashboard quick links to a single four-column row and the semester review lists to scannable rows with template icons, semester chips and an unread marker.",
          "Confirmation dialogs now name what they act on and state the consequences instead of a generic “are you sure”.",
        ],
        Infrastructure: [
          "V48__Migrate_profession_columns_to_varchar.sql: INFORMATIKER/MEDIAMATIKER → INFORMATIK/MEDIAMATIK, ENUM columns changed to VARCHAR(50).",
          "V49__Add_notification_preferences.sql: notification_preferences table with per-user flags for insufficient grades, monthly and semester reports.",
          "V50__Add_user_professions.sql: user_professions join table, backfill from users.profession_type including expansion of the legacy BOTH value; idempotent and safe to re-run.",
          "Flyway is disabled in this project, so the migrations are applied by hand; V50 documents the execution path and the MariaDB client pitfall that reports error 1064 when a script is sent as a single statement.",
        ],
      },
    },
    {
      version: "v6.2",
      date: "July 29, 2026",
      sections: {
        Backend: [
          "[US71] Switched the identity of a confirmation to learnerId + classId + semesterId: RevisorService fully reworked for class scope (dashboard, detail, check, reject, delete).",
          "[US71.1] RevisorController accepts an optional classId query parameter on all entry-scoped endpoints (GET/DELETE /revisor/reviews/{learnerId}/{semesterId}, .../check, .../reject).",
          "[US72] Reduced the review status to a single positive outcome: CHECKED. RevisorReviewStatus.FINALIZED is deprecated and only deserialised for rows missed by the migration.",
          "[US72.1] Removed the endpoints POST /revisor/reviews/{learnerId}/{semesterId}/finalize and the legacy alias POST /revisor/{id}/finalize.",
          "[US73] Extended ReportDocument with archivedAt and archivedBy; added archiveReport/restoreReport to ReportService; ReportMetadataDTO exposes archivedAt and isArchived().",
          "[US73.1] New endpoints POST /me/reports/{id}/archive and POST /me/reports/{id}/restore; DELETE /me/reports/{id} now responds with 403 REPORT_DELETE_NOT_ALLOWED.",
          "[US73.2] New admin endpoint DELETE /admin/reports/{id} with @PreAuthorize(\"hasRole('ADMIN')\") for permanent deletion.",
          "[US74] SemesterGradeConfirmationService verifies before confirming that a definitive grade is saved for every subject (error codes NO_SUBJECTS and DEFINITIVE_GRADES_NOT_SAVED including the list of missing subjects).",
          "[US74.1] Extended SemesterConfirmationStatusDTO with reportUploaded, reportFilename, reportUploadedAt as well as reviewedAt and finalizedAt.",
          '[US75] UserController returns Map.of("message", …) instead of MessageResponse for profession updates, giving the client consistent JSON.',
          "[US76] New tests: UserControllerTest and RevisorMultiClassReviewIntegrationTest; extended ReportServiceTest, RevisorWorkflowIntegrationTest and SemesterGradeConfirmationServiceTest.",
        ],
        Frontend: [
          "[US71] revisorService passes classId through to getDetail, markChecked, reject and deleteReviewEntry (as a query parameter; behaviour is unchanged without a classId).",
          "[US71.1] RevisorReviewsPage and RevisorDashboardPage carry the class of an entry, so two classes of the same apprentice in the same semester are handled separately.",
          "[US72] Added the helper isCheckedStatus(status) in revisorUtils, which collapses CHECKED and the legacy FINALIZED; statusBadgeClasses adjusted accordingly.",
          "[US73] Replaced reportService.deleteReport with archiveReport and restoreReport; ReportsList distinguishes active and archived documents including the matching actions.",
          "[US74] SemesterConfirmationCard shows report card metadata, review stamps and the error messages for missing definitive grades; StudentGrades passes the class context through.",
          "[US76] New test file revisorService.test.js; considerably extended tests for RevisorReviewsPage, SemesterConfirmationCard and ReportsList.",
        ],
        "UI / UX": [
          "Unified status colours: CHECKED is now green (previously amber), REJECTED gets its own red badge.",
          "Switched the report card actions for apprentices from “delete” to “archive” with restore.",
          "The confirmation card makes the flow explicit: save definitive grades, upload the report card, confirm.",
        ],
        Infrastructure: [
          "V45__Unique_confirmation_per_learner_class_semester.sql: backfill of class_id, archiving and removal of duplicates, unique index uk_confirmation_learner_class_semester; creates the audit tables semester_grade_confirmations_archive and semester_grade_confirmation_issues.",
          "V46__Collapse_finalized_into_checked.sql: FINALIZED → CHECKED including carrying over missing reviewed_at / reviewed_by from the finalized stamps.",
          "V47__Archive_reports_instead_of_delete.sql: columns archived_at and archived_by_user_id on report_document.",
          "V47__Migrate_profession_columns_to_varchar.sql: INFORMATIKER/MEDIAMATIKER → INFORMATIK/MEDIAMATIK, ENUM columns changed to VARCHAR(50).",
          "All migrations are idempotent and non-destructive; target platform is MariaDB 10.3+ (notes for MySQL 8 are contained in the files).",
        ],
      },
    },
    {
      version: "v6.1",
      date: "June 15, 2026",
      sections: {
        Backend: [
          "[US66] Semester notes feature: added the SemesterNote entity, controller (CRUD), service, repository and DTOs as well as Flyway migrations V42/V43.",
          "[US67] Extended practice instructor assignment: learner search (LearnerSearchResultDTO), assign-learner endpoint and UserRepository queries by specialisation/profession type.",
          "[US68] Extended BonusService with semester percent override handling and adjusted BonusOverrideRequest and the related DTOs.",
          "[US70] Added comprehensive server-side test coverage: ~14 service/integration tests (auth, bonus, grade, RevisorWorkflow, SemesterNote and others) including JaCoCo.",
        ],
        Frontend: [
          "[US66] Implemented semesterNoteService and SemesterNotesModal; integrated into StudentList and SemesterReviewDetailPage.",
          "[US67] Implemented AssignLearnerModal and bulk assignment in PracticeInstructorAssignments.",
          "[US68] Refactored MyBonusPage and BonusStudentDetailPage onto a shared SemesterCard.",
          "[US69] Semester reviews rendered in LearnerDashboard and navigation depending on the profession type.",
          "[US70] Added ~40 Jest tests using renderWithProviders and a recharts mock.",
        ],
        "UI / UX": [
          "[US66] Made quick notes per semester directly accessible in the apprentice list and the semester review.",
          "[US69] Unified the profile dropdown, password modal and validation texts (English).",
          "Stabilised ClassManagement and SubjectManagement; improved performance via useMemo.",
        ],
        Infrastructure: [
          "[US70] Established JaCoCo coverage in the backend and a Jest test setup (renderWithProviders, recharts patch) in the frontend.",
          "[US66] Added Flyway migrations V42/V43 for semester notes.",
        ],
      },
    },
    {
      version: "v6.0",
      date: "June 1, 2026",
      sections: {
        Backend: [
          "[US63] Rebuilt BonusController and BonusService and introduced bonus DTOs, repositories and entities for periods, semesters, overrides and configurations.",
          "[US63] Added new endpoints for bonus scope and visible apprentices so that roles only see relevant bonus data.",
          "[US63] Unified bonus calculation based on checked/finalized confirmations; recalculation preserves existing semester overrides.",
          "[US63] Added ReviewPercentageCalculationService and aligned the existing calculation/review flows with it.",
        ],
        Frontend: [
          "[US64] Implemented new bonus pages: BonusLandingPage, MyBonusPage, BonusStudentListPage and BonusStudentDetailPage.",
          "[US64] Integrated bonus navigation and redirect logic by bonus group into the navigation/app wiring.",
          "[US64] Adjusted the StatusBadge logic so that overridden states are consistently treated as completed.",
          "[US64] Extended the bonus display with semester and amount details; cleaned up debug and legacy logic.",
          "[US65] Connected the new explanatory video page and linked it in the footer; content is presented separately by role.",
        ],
        "UI / UX": [
          "[US64] Structured the bonus flows clearly along the roles (own view, apprentice view, detail view).",
          "[US65] Extended the footer navigation: explanatory videos are centrally discoverable and grouped by role.",
          "[US64] Visualised bonus information per semester more transparently and understandably.",
        ],
        Infrastructure: [
          "Added the SQL helper script `FIX_BONUS_CALCULATIONS.sql` to correct faulty earlier bonus figures in a targeted way.",
          "Cleaned up the CI configuration in the context of the GitHub mirror sections.",
        ],
      },
    },
    {
      version: "v5.6",
      date: "April 23, 2026",
      sections: {
        Backend: [
          "[US55] Added the reviewer role server-side: controller, service, review status, access control and admin access to reviewer endpoints.",
          "[US55.1] Modelled and persisted semester grade confirmation and definitive semester grades and exposed them through dedicated controllers.",
          "[US55.2] Implemented reviewer rejections including reject fields, status transitions and Flyway migrations.",
          "[US56] Extended report upload: report cards can be uploaded per semester and used in review/confirmation flows.",
          "[US57] Extended password management: change password, direct reset, public forced change and a force password reset flag on the user.",
          "[US57.1] Added AdminStudentController for admin password reset and adjusted OnboardingFilter for the forced password change.",
          "[US58] Extended invitation/registration: reviewer role allowed for global links, username validated and practice instructor department stored.",
          "[US59] Improved LDAP login: evaluated the auth status and synchronised the local password after a successful LDAP login.",
          "[US60] Added class deletion analysis: impact DTO, repository queries and service logic for affected apprentices/assignments.",
          "[US61] Automated semester creation based on the training duration and increased the page size for apprentice/class member endpoints.",
        ],
        Frontend: [
          "[US55] Implemented RevisorDashboardPage and RevisorReviewsPage, including review lists, detail view and status actions.",
          "[US55.1] Added SemesterConfirmationCard to the grade view so semesters can be confirmed definitively and handed over to reviewers.",
          "[US56] Extended StudentGradeReportModal and ReportsList to make semester-related report cards visible and usable.",
          "[US57] Integrated PasswordManagementModal, ForcePasswordChangePage, ResetLearnerPasswordModal and PasswordStrengthIndicator.",
          "[US57.1] Reworked registration and InviteRegister with password strength, additional validation and improved error handling.",
          "[US58] Adjusted InvitationsPage and GenerateInviteModal for reviewer invitations and extended invite data.",
          "[US59] StudentList and StudentDetail merge instructor data from multiple sources and display assignments more consistently.",
          "[US60] ClassManagement shows an impact preview of the affected data before deletion.",
          "[US61] ClassManagement and SchoolManagementUnified create semesters automatically based on the training duration.",
          "[US62] Adjusted navigation, ProtectedRoute and AuthContext for the new roles, the forced password change and mobile navigation.",
        ],
        "UI / UX": [
          "Brought the grade, report and review flows closer together so that semester completions are easier to follow.",
          "Unified password and invitation texts in English and made error states easier to understand.",
          "Aligned terminology in the dashboard, navigation, apprentice management and profiles more consistently to Apprentice.",
          "Made subject filters, subject selection and semester tabs more stable and easier to operate for everyday grade maintenance.",
        ],
        Infrastructure: [
          "Added migrations for reviewer rejections and the practice instructor department.",
          "Increased upload limits for larger report and report card files.",
          "Extended input validation with tests for the new password/registration validation.",
        ],
      },
    },
    {
      version: "v5.5",
      date: "April 7, 2026",
      sections: {
        Backend: [
          "[US49] Investigated the monthly grade report in production: added debug logging for professionType, dateFrom, dateTo, timezone, number of grades found and the preview/render payload.",
          "[US49.1] Identified the root cause in the production monthly report flow and corrected it minimally so the same real data basis as locally is used.",
          "[US49.2] Unified preview and final mail rendering onto the same correctly filtered data set.",
          "[US50] Semester meeting: extended the fetch logic for previous meetings so the latest completed reviews of the same apprentice are loaded.",
          "[US50.1] Extended the DTO with previousAnswers / placeholder values without overwriting current input.",
          "[US51] Extended the semester meeting data model per question: answer + comment are stored persistently.",
          "[US51.1] Integrated comments into GET/SAVE responses and the placeholder source.",
          "[US52] Fixed the endpoint/service for selecting previous semester meetings: correct learnerId filter, exclusion of the current meeting, newest-first sorting.",
        ],
        Frontend: [
          "[US49] Verified the production bug in the monthly grade report and ensured the real monthly grades are shown in the preview and the mail.",
          "[US50] The semester meeting shows earlier answers as subtle placeholders, analogous to the training report.",
          "[US51] Added an additional comment field per yes/no question in the semester meeting (optional, multiline, usable continuously).",
          "[US51.1] Previous comments can be shown as placeholders to create continuity.",
          "[US52] Connected the dropdown “source for goal placeholders” correctly to previous semester meetings; the selection now loads real entries instead of permanently showing “No previous meetings”.",
          "[US52.1] Cleaned up the mapping and display of the dropdown options for previous meetings.",
          "[US53] Fixed the select styling in the semester meeting: removed the duplicate arrow, clean appearance with a single dropdown indicator.",
          "[US54] Applied the same dropdown fix in the training report.",
        ],
        "UI / UX": [
          "Brought the semester meeting functionally closer to the training report so earlier content can be reused more easily.",
          "Continuous commenting per question improves real-world usability throughout the year instead of only at completion.",
          "Unified the look of dropdowns in the review/report area and made them more professional.",
        ],
      },
    },
    {
      version: "v5.4",
      date: "March 23, 2026",
      sections: {
        Backend: [
          "[US46] Fixed an error when generating invitation links (token creation, validation).",
          "[US47] Implemented the delete endpoint for invitation links.",
          "[US48] Extended the invitation logic: creating a link can be tied directly to an apprentice assignment.",
          "[US48.1] Automatic assignment of a practice instructor to the apprentice after successful registration via the generated link.",
        ],
        Frontend: [
          "[US47] Integrated the delete action for invitations in the UI (including button and feedback state).",
          "[US48] Added the option to generate a new invitation link directly when assigning an apprentice.",
          "[US48.1] Adjusted the UI flow so that generation + assignment work as one coherent process.",
        ],
        "UI / UX": [
          "Extended invitation management so links can be created directly in the context of assignments.",
          "Clearer workflow: fewer steps between the invitation and the actual assignment of a practice instructor.",
        ],
      },
    },
    {
      version: "v5.3",
      date: "March 17, 2026",
      sections: {
        Backend: [
          "[US43] Extended the invitations module: secure invitation links for vocational trainers and practice instructors can be created, stored and managed as active/persistent.",
          "[US43.1] Added metadata for invitations (role, creation date, expiry status, persistence).",
          "[US44] Improved the redirect state for semester reviews so the last selected apprentice and context are preserved when navigating back.",
        ],
        Frontend: [
          "[US43] Implemented the new invitations page with header, active invitations list, status badges and copy actions.",
          "[US43.1] Integrated the UI for creating new invitations.",
          "[US43.2] Added information cards explaining how invitations work, their lifecycle and recommended usage.",
          "[US45] Made the instructor timeline more compact: reduced spacing, denser row structure and improved readability.",
          "[US45.1] Highlighted unassigned areas in the timeline in orange so open assignments are immediately visible.",
          "[US44] Adjusted the semester review navigation so back correctly returns to the previously opened tab of the apprentice.",
        ],
        "UI / UX": [
          "Designed the new invitation page in the Journey style so invitation management looks clearer and more professional.",
          "Made the instructor timeline clearer without losing functionality.",
          "Considerably improved the navigation flow in reviews so apprentices do not have to be searched again.",
        ],
      },
    },
    {
      version: "v5.2",
      date: "March 9, 2026",
      sections: {
        Backend: [
          "[US43] Reworked the email configuration: correct sender/from header set for outgoing mail.",
          "[US43.1] Unified and validated reply-to / display name in the mail service.",
          "[US44] Prepared mail rendering so new HTML templates can be loaded centrally and filled with variables.",
          "[US45] Prepared instructor timeline data: sorted events, consistent timestamps and more robust API output.",
        ],
        Frontend: [
          "[US46] Embedded PlanPilot cleanly via iframe: responsive container, height control and better fit into the Journey layout.",
          "[US46.1] Prepared a fallback/notice state for iframe problems or blocked embedding.",
          "[US44] Designed the new professional email template and integrated it into the preview/test flow.",
          "[US45] Reworked the instructor timeline visually: better grouping, clearer cards and improved readability.",
          "[US45.1] Gave timeline entries a stronger visual hierarchy, more compact spacing and better status feedback.",
        ],
        Infrastructure: [
          "[US43] Checked the SMTP/mail setup and corrected the header behaviour for production emails.",
          "[US44] Unified the template structure for HTML mails so future mail types can be added more easily.",
        ],
        "UI / UX": [
          "Embedding external tools feels more integrated and less like a foreign system.",
          "Emails were made visually more appealing and more professional.",
          "The instructor timeline is clearer, more compact and quicker to grasp.",
        ],
      },
    },
    {
      version: "v5.1",
      date: "February 16, 2026",
      sections: {
        Backend: [
          "[US38] Training report: determine the last remarks per apprentice and provide them as placeholder data (read-only, no automatic overwriting).",
          "[US39] Training report: implemented the bonus calculation (8=exceeded, 5=met, 3=barely sufficient, 0=insufficient) including summation and persistence.",
          "[US40] Semester meeting: extended the data model (apprenticeship year, date) and removed/deprecated the signature field.",
        ],
        Frontend: [
          "[US38] Training report: implemented placeholders for remarks from the last report in the form (the user can change/overwrite them).",
          "[US39] Training report: displayed the live bonus total in the UI and coupled it to the assessment selection (8/5/3/0).",
          "[US41] Semester meeting: integrated a checkbox/toggle for the overall assessment in the form.",
          "[US40] Semester meeting: added the apprenticeship year and date fields to the form; removed the signature field.",
          "[US42] Autosave: implemented saving on tab change/unload (best effort) and/or added a warning popup for unsaved changes.",
        ],
        "UI / UX": [
          "Made forms more robust: less data loss thanks to autosave/warnings.",
          "The training report can be filled in faster thanks to sensible placeholders from the last report.",
          "The semester meeting is clearer and shorter by removing the signature and adding required info (apprenticeship year, date).",
        ],
      },
    },
    {
      version: "v5.0",
      date: "February 11, 2026",
      sections: {
        Backend: [
          "[U31] Implemented the data model for practice instructor assignment: an apprentice has exactly 1 active assignment, the history is stored with versioning.",
          "[U31] API for assigning/switching including validation (only active practice instructors, atomic switch, history write).",
          "[U32] Access protection for apprentices: the directory only returns assigned apprentices, detail endpoints deny access without an active assignment (also via URL).",
          "[U33] Query logic for past reviews: a practice instructor only sees their own completed reviews; no foreign/future ones.",
          "[U34] Limited read-only grade endpoints for practice instructors to assigned apprentices, integrated the semester filter.",
          "[U35] Review creation only allowed server-side with an active assignment; after a switch writes are blocked while existing reviews stay readable.",
          "[U36] Stored the user setting for simple/expert view (persisted per user, default defined).",
          "[U37] Centralised the naming standard for semester reviews (the backend returns consistent labels).",
          "Report card upload: implemented storage/DB metadata and download/read rights (role-based).",
          "Professions: consistent modelling/validation and filter logic on the relevant endpoints.",
          "LDAP login: corrected the mapping/fetching of the correct display name (e.g. givenName/sn or displayName).",
        ],
        Frontend: [
          "[U31] UI for vocational trainers: assign apprentices to a practice instructor (dropdown, active practice instructors, switching possible, history visible).",
          "[U32] Student directory for practice instructors: shows only currently assigned apprentices; direct navigation to other apprentices is intercepted.",
          "[U33] View for past reviews: only own ones, read-only display, clearly marked (archived).",
          "[U34] Grade view for practice instructors: read-only, semester filter, only assigned apprentices.",
          "[U35] Review creation in the UI only enabled when an assignment exists; disabled with a hint text after a switch.",
          "[U36] Toggle simple view vs. expert view in the navigation; the preference is stored and applied on reload.",
          "[U37] Adjusted unified titles/labels for semester reviews in the UI.",
          "Report card upload: upload component, file list, download/preview (role-dependent).",
          "Professions: UI filters/badges and consistent display in profile/lists.",
          "LDAP login: name/profile header displayed correctly after login.",
        ],
        "UI / UX": [
          "Clear notices when an assignment is missing (no access, no review creation).",
          "Consistent naming and better orientation for reviews by period/semester.",
          "Simple view reduces navigation to student directory, learning documentation and reviews; expert view shows all modules.",
        ],
      },
    },
    {
      version: "v4.1",
      date: "January 13, 2026",
      sections: {
        Backend: [
          "[US30] Defined the unified grade colour scale centrally and made it usable system-wide.",
          "[US31] Implemented average calculation across all semesters per subject.",
          "[US32] Unified rounding of all grade averages to two decimal places.",
          "[US33] Implemented the mail service for automatic notifications.",
          "[US33.1] Single mail for an insufficient grade (triggered per grade entry).",
          "[US33.2] Monthly report with all grades (cron job: last day of the month).",
          "[US33.3] Storage of an email history (timestamp, type, recipient).",
          "[US34] Corrected role and profession filters for vocational trainer views (IT only sees IT).",
        ],
        Frontend: [
          "[US30] Centralised the grade colour palette and applied it in all components.",
          "[US31] New analytics page with subject averages across all semesters.",
          "[US31.1] Integrated a chart with all grades analogous to the admin view.",
          "[US35] Implemented navigation between apprentices in the student detail view (next / previous).",
          "[US36] Added an alias field for learning documentation links in the UI.",
          "[US37] Prepared the toggle/decision logic for sidebar vs. topbar.",
        ],
        Infrastructure: [
          "[US33] Set up the SMTP configuration for automated mails.",
          "[US33.2] Configured the scheduled job for monthly reports.",
        ],
        "UI / UX": [
          "Consistent visual presentation of grades across all pages.",
          "Improved orientation in detail views through linear navigation.",
        ],
      },
    },
    {
      version: "v4.0",
      date: "December 3, 2025",
      sections: {
        Backend: [
          "[US27] Created the data model for semester meetings (versioning, relations, history).",
          "[US27] Implemented the CRUD API for meetings.",
          "[US25] Created the API endpoint for storing the learning documentation link.",
          "[US29] Implemented PDF export for completed meetings.",
          "[US29] Added status logic (draft, in progress, completed) in the backend.",
        ],
        Frontend: [
          "[US25] UI field for storing an external learning documentation link in the apprentice profile.",
          "[US26] Display and opening of the learning documentation link for trainers in the profile.",
          "[US28] Form for semester meetings: goals, strengths, measures, free text.",
          "[US28] Validation, autosave/draft and structured input fields.",
          "[US29] Status display in the UI (draft, in progress, completed).",
          "[US29] Integrated the button for PDF export.",
        ],
        "UI / UX": [
          "Consistent presentation of the learning documentation in the profile.",
          "Optimised form design for structured meeting recording.",
        ],
      },
    },
    {
      version: "v3.2",
      date: "November 20, 2025",
      sections: {
        Backend: [
          "[US24] Integrated the default filter for the current semester into the grade endpoints.",
          "[US25] Introduced simple caching for profile endpoints.",
        ],
        Frontend: [
          "[US19.1] Defined layout container, grid and breakpoints for mobile, tablet and desktop.",
          "[US19.2] Created offcanvas navigation with burger menu, overlay and animation.",
          "[US19.3] Displayed tables as a card view on mobile (padding, visibility, readability).",
          "[US20] Added a search field for apprentices in the overview.",
          "[US21] Reworked and unified the icon for the lowest grade.",
          "[US22] Editable text labels for the grade scale (e.g. “excellent” instead of “very good”).",
          "[US23] Implemented an overall chart for all grades per apprentice.",
          "[US24] Implemented automatic preselection of the current semester in the UI.",
        ],
        "UI / UX": [
          "[US26] Renamed the “Change Log” section to “Release Log” and added version 3.2.",
        ],
      },
    },
    {
      version: "v3.1",
      date: "November 3, 2025",
      sections: {
        Backend: [
          "[US17.1–17.4] Implemented LDAP login (SSO with Sunrise email) including ENV setup, timeout handling and fallback to local users.",
          "[US17.3] Made role mapping from LDAP groups (“bb”, “pb”, “ln”) configurable and defined a default role for new users.",
          "[US24] Integrated the default filter “current semester” into the grade endpoints.",
          "[US25] Introduced simple caching for profile endpoints to reduce the load time of profile pages.",
        ],
        Frontend: [
          "[US18] Implemented a chart component for the grade history per subject and integrated it into the grade overview.",
          "[US23] Added an overall chart for all grades of an apprentice.",
          "[US19.1–19.3] Responsive layout with breakpoints, offcanvas navigation (burger menu) and a card view for tables on mobile.",
          "[US20] Added a search field for apprentices in the overview.",
          "[US21] Reworked and unified the icon for the lowest grade.",
          "[US22] Configurable text labels for the grade scale (e.g. changing “very good” → “excellent”).",
          "[US24] Implemented automatic preselection of the current semester in the grade UI.",
        ],
        Infrastructure: [
          "[US17.1] Configured ENV variables for the LDAP server, DN, bind user and password.",
          "[US17.5] Documented the tech readme and an ENV example for the LDAP setup.",
        ],
        "UI / UX": [
          "[US26] Renamed the “Change Log” section to “Release Log” and added version 3.1.",
        ],
      },
    },
    {
      version: "v3.0",
      date: "October 16, 2025",
      sections: {
        Backend: [
          "[US12] Implemented the import mechanism for the module toolkit (global subjects, duplicate handling, error logging).",
          "[US13] Created the CRUD API for global subject management by vocational trainers.",
          "[US14] Implemented the m:n relation between semester and subject for apprentices (data model + persistence).",
          "[US15] Implemented the average calculation per subject and semester in the backend.",
          "[US16] Aggregated class overview with averages per subject and semester and a comparison between semesters.",
        ],
        Frontend: [
          "[US13] UI for global subject management (table with actions, validation, duplicate check).",
          "[US14] UI for selecting subjects per semester (checkbox lists, stored per semester).",
          "[US15] Display of the calculated grade averages in the dashboard (table / simple visualisation).",
          "[US16] Class overview with semester comparison and table view for vocational trainers.",
        ],
        Infrastructure: [
          "Set up the env configuration and dev server for the local development workflow.",
        ],
      },
    },
    {
      version: "v2.0",
      date: "September 25, 2025",
      sections: {
        Backend: [
          "[US06] Created the role model (RBAC: apprentices, practice instructors, vocational trainers, admin) in the database.",
          "[US07] Implemented role assignment during registration and profile editing.",
          "[US08] Added the field “profession” (mediamatician / IT specialist) to the profile.",
          "[US09] Implemented CRUD endpoints for classes.",
          "[US10] Implemented the assignment of apprentices to classes (relations).",
          "[US11] Implemented global subject management for vocational trainers.",
          "[US12] Created the grade model with date, subject and grade.",
        ],
        Frontend: [
          "[US07] Integrated the role selection in the UI.",
          "[US08] Selection and display of the profession in the profile.",
          "[US09] Class list with create, edit and delete.",
          "[US10] UI for assigning apprentices to classes.",
          "[US11] UI for global subject management.",
          "[US12] Form for grade entry with validation and display.",
        ],
        "UI / UX": [
          "Introduced role-based navigation for ADMIN, VOCATIONAL TRAINER and APPRENTICE.",
          "Optimised responsive navigation and layout with Tailwind CSS.",
        ],
      },
    },
    {
      version: "v1.0",
      date: "September 10, 2025",
      sections: {
        Backend: [
          "[US01] Project setup: React frontend, Spring Boot API and MySQL connection.",
          "[US02] Basic database model (user, role) with Flyway migrations.",
          "[US03] Registration with validation and hashed password.",
          "[US04] Login & JWT authentication with error handling.",
        ],
        Frontend: [
          "[US01] Built the basic frontend layout.",
          "[US03] Registration form with master data.",
          "[US04] Login form with token handling.",
          "[US05] Profile page showing the master data of signed-in users.",
        ],
      },
    },
  ];

  const getVersionIcon = (type) => {
    switch (type) {
      case "major":
        return "fas fa-rocket";
      case "minor":
        return "fas fa-plus-circle";
      case "patch":
        return "fas fa-wrench";
      case "initial":
        return "fas fa-flag";
      default:
        return "fas fa-code-branch";
    }
  };

  const getVersionColor = (type) => {
    switch (type) {
      case "major":
        return "from-sunrise-red to-sunrise-red/80";
      case "minor":
        return "from-slate-cloud to-slate-cloud/80";
      case "patch":
        return "from-blue-600 to-blue-700";
      case "initial":
        return "from-slate-cloud to-slate-cloud/80";
      default:
        return "from-gray-600 to-gray-700";
    }
  };

  const filteredReleaseEntries =
    selectedVersion === "all"
      ? releaseEntries
      : releaseEntries.filter((e) => e.version === selectedVersion);

  const filteredChangeEntries =
    selectedVersion === "all"
      ? changeLogEntries
      : changeLogEntries.filter((e) => e.version === selectedVersion);

  const renderReleaseMode = () => (
    <div className="relative">
      {/* Timeline Line */}
      <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-sunrise-red hidden md:block" />

      {filteredReleaseEntries.map((entry) => (
        <div key={entry.version} className="relative mb-12 last:mb-0">
          {/* Timeline Dot */}
          <div
            className="absolute left-6 w-4 h-4 bg-white border-4 border-sunrise-red rounded-full hidden md:block"
            style={{ top: "1.5rem" }}
          />

          {/* Content Card */}
          <div className="md:ml-16">
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200">
              {/* Card Header */}
              <div
                className={`bg-gradient-to-r ${getVersionColor(
                  entry.type,
                )} text-white px-6 py-6`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
                      <i
                        className={`${getVersionIcon(
                          entry.type,
                        )} text-white text-xl`}
                      />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">{entry.version}</h2>
                      <p className="text-white/80">{entry.date}</p>
                    </div>
                  </div>
                  <div className="hidden sm:flex flex-col items-end space-y-1 text-right">
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium capitalize">
                      {entry.type} Release
                    </span>
                    <span className="text-xs text-white/70">Release Notes</span>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6 space-y-5">
                <p className="text-gray-700 leading-relaxed">{entry.goal}</p>

                {entry.highlights.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-grey-dusk mb-2 flex items-center">
                      <i className="fas fa-star text-sunrise-red mr-2" />
                      Key New Features
                    </h3>
                    <ul className="space-y-2">
                      {entry.highlights.map((item, i) => (
                        <li key={i} className="flex items-start">
                          <div className="w-2 h-2 bg-sunrise-red rounded-full mt-2 mr-3 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {entry.improvements.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-grey-dusk mb-2 flex items-center">
                      <i className="fas fa-arrow-up text-sunrise-red mr-2" />
                      Improvements
                    </h3>
                    <ul className="space-y-2">
                      {entry.improvements.map((item, i) => (
                        <li key={i} className="flex items-start">
                          <div className="w-2 h-2 bg-slate-cloud rounded-full mt-2 mr-3 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {entry.fixes.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-grey-dusk mb-2 flex items-center">
                      <i className="fas fa-wrench text-sunrise-red mr-2" />
                      Technical Changes & Fixes
                    </h3>
                    <ul className="space-y-2">
                      {entry.fixes.map((item, i) => (
                        <li key={i} className="flex items-start">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="pt-2 flex flex-wrap gap-2 text-xs text-gray-500">
                  <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded-full">
                    <i className="fas fa-calendar-alt mr-1" />
                    {entry.date}
                  </span>
                  <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded-full">
                    <i className="fas fa-tag mr-1" />
                    {entry.type.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderChangeMode = () => (
    <div className="space-y-8">
      {filteredChangeEntries.map((entry) => (
        <div
          key={entry.version}
          className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
        >
          <div
            className={`bg-gradient-to-r ${
              entry.version === "v1.0"
                ? getVersionColor("initial")
                : entry.version === "v3.0" || entry.version === "v2.0"
                  ? getVersionColor("major")
                  : getVersionColor("minor")
            } text-white px-6 py-4 flex items-center justify-between`}
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mr-3">
                <i className="fas fa-code-branch text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">
                  Change Log – {entry.version}
                </h2>
                <p className="text-white/80 text-sm">{entry.date}</p>
              </div>
            </div>
            <span className="hidden sm:inline-flex bg-white/15 px-3 py-1 rounded-full text-xs font-medium">
              Technical Changes
            </span>
          </div>
          <div className="p-6 space-y-6">
            {Object.entries(entry.sections).map(([sectionName, items], idx) => (
              <div key={idx}>
                <h3 className="text-md font-semibold text-grey-dusk mb-2 flex items-center">
                  <i className="fas fa-layer-group mr-2 text-sunrise-red" />
                  {sectionName}
                </h3>
                <ul className="space-y-1 list-disc list-inside text-gray-700 text-sm">
                  {items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-sunrise-red to-sunrise-red/80 rounded-2xl flex items-center justify-center mr-4">
              <i className="fas fa-history text-white text-2xl" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-grey-dusk">
                Release & Change Log
              </h1>
            </div>
          </div>
          <div className="w-24 h-1 bg-gradient-to-r from-sunrise-red to-sunrise-red/60 rounded-full mx-auto" />
        </div>

        {/* Controls: Mode Switcher + Version Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          {/* Mode Switcher */}
          <div className="flex justify-center sm:justify-start">
            <div className="inline-flex bg-white rounded-full shadow-md p-1">
              <button
                type="button"
                onClick={() => setMode("release")}
                className={`px-4 py-2 text-sm font-medium rounded-full transition ${
                  mode === "release"
                    ? "bg-sunrise-red text-white shadow"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                Release Notes
              </button>
              <button
                type="button"
                onClick={() => setMode("change")}
                className={`px-4 py-2 text-sm font-medium rounded-full transition ${
                  mode === "change"
                    ? "bg-slate-cloud text-white shadow"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                Change Log
              </button>
            </div>
          </div>

          {/* Version Filter */}
          <div className="flex justify-center sm:justify-end">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600 uppercase tracking-wide">
                Version
              </span>
              <select
                value={selectedVersion}
                onChange={(e) => setSelectedVersion(e.target.value)}
                className="text-sm border border-gray-300 rounded-full px-3 py-1 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-sunrise-red/60"
              >
                {versions.map((v) => (
                  <option key={v} value={v}>
                    {v === "all" ? "All Versions" : v}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Info Text */}
        <div className="mb-8 text-center text-sm text-gray-600">
          {mode === "release" ? (
            <p>
              In the <span className="font-semibold">Release Log</span> you see,
              what has changed for users with each version.
            </p>
          ) : (
            <p>
              In the <span className="font-semibold">Change Log</span> you can
              find the technical changes per version, grouped by sections.
            </p>
          )}
        </div>

        {/* Content */}
        {mode === "release" ? renderReleaseMode() : renderChangeMode()}
      </div>
    </div>
  );
}

export default Changelog;
